import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import {
  generateSessionFlags,
  generateSessionSuggestions,
  prepareSessionTrendData
} from '../../utils/dashboardSharedUtils';
import SessionListPanel from './SessionListPanel';

function calculatePeriodMetrics(sessions, periodType = 'weekly') {
  if (sessions == null || !Array.isArray(sessions) || sessions.length === 0) {
    return null;
  }

  const now = new Date();
  let periodStart, periodEnd, previousPeriodStart, previousPeriodEnd;

  if (periodType === 'weekly') {
    periodEnd = new Date(now);
    periodStart = new Date(now);
    periodStart.setDate(periodStart.getDate() - 7);
    previousPeriodEnd = new Date(periodStart);
    previousPeriodStart = new Date(periodStart);
    previousPeriodStart.setDate(previousPeriodStart.getDate() - 7);
  } else {
    periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    previousPeriodStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    previousPeriodEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  }

  const validSessions = Array.isArray(sessions) ? sessions : [];
  const currentPeriodSessions = validSessions.filter(s => {
    if (!s.session_date) return false;
    const sessionDate = new Date(s.session_date);
    return sessionDate >= periodStart && sessionDate <= periodEnd;
  });

  const previousPeriodSessions = validSessions.filter(s => {
    if (!s.session_date) return false;
    const sessionDate = new Date(s.session_date);
    return sessionDate >= previousPeriodStart && sessionDate <= previousPeriodEnd;
  });

  if (currentPeriodSessions.length === 0) {
    return null;
  }

  const currentMetrics = {
    emotion: currentPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.emotion_score) || 0), 0) / currentPeriodSessions.length,
    motivation: currentPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.motivation_score) || 0), 0) / currentPeriodSessions.length,
    burnout: currentPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.burnout_score) || 0), 0) / currentPeriodSessions.length,
    engagement: currentPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.engagement_score) || 0), 0) / currentPeriodSessions.length,
    stress: currentPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.stress_score) || 0), 0) / currentPeriodSessions.length,
    wellbeing: currentPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.wellbeing_score) || 0), 0) / currentPeriodSessions.length,
    totalSessions: currentPeriodSessions.length
  };

  const previousMetrics = previousPeriodSessions.length > 0 ? {
    emotion: previousPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.emotion_score) || 0), 0) / previousPeriodSessions.length,
    motivation: previousPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.motivation_score) || 0), 0) / previousPeriodSessions.length,
    burnout: previousPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.burnout_score) || 0), 0) / previousPeriodSessions.length,
    engagement: previousPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.engagement_score) || 0), 0) / previousPeriodSessions.length,
    stress: previousPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.stress_score) || 0), 0) / previousPeriodSessions.length,
    wellbeing: previousPeriodSessions.reduce((sum, s) => sum + (parseFloat(s.wellbeing_score) || 0), 0) / previousPeriodSessions.length,
    totalSessions: previousPeriodSessions.length
  } : null;

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const changes = previousMetrics ? {
    emotion: calculateChange(currentMetrics.emotion, previousMetrics.emotion),
    motivation: calculateChange(currentMetrics.motivation, previousMetrics.motivation),
    burnout: calculateChange(currentMetrics.burnout, previousMetrics.burnout),
    engagement: calculateChange(currentMetrics.engagement, previousMetrics.engagement),
    stress: calculateChange(currentMetrics.stress, previousMetrics.stress),
    wellbeing: calculateChange(currentMetrics.wellbeing, previousMetrics.wellbeing),
    sessions: calculateChange(currentMetrics.totalSessions, previousMetrics.totalSessions)
  } : null;

  const flags = [];
  currentPeriodSessions.forEach(session => {
    const burnout = parseFloat(session.burnout_score) || 0;
    const stress = parseFloat(session.stress_score) || 0;
    const motivation = parseFloat(session.motivation_score) || 0;
    const emotion = parseFloat(session.emotion_score) || 0;
    if (burnout > 70) flags.push({ type: 'High Burnout', severity: 'high', date: session.session_date, value: burnout.toFixed(1) });
    if (stress > 75) flags.push({ type: 'High Stress', severity: 'high', date: session.session_date, value: stress.toFixed(1) });
    if (motivation < 30) flags.push({ type: 'Low Motivation', severity: 'medium', date: session.session_date, value: motivation.toFixed(1) });
    if (emotion < 30) flags.push({ type: 'Low Emotion', severity: 'medium', date: session.session_date, value: emotion.toFixed(1) });
  });

  const topFlags = flags.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 3);

  const generateInsight = () => {
    if (!changes) return 'No previous period data available for comparison.';
    const wellbeingChange = changes.wellbeing;
    const burnoutChange = changes.burnout;
    const motivationChange = changes.motivation;
    if (wellbeingChange && wellbeingChange > 5) {
      return `Your wellbeing improved ${wellbeingChange.toFixed(1)}% compared to the previous ${periodType === 'weekly' ? 'week' : 'month'}. Keep up the great work!`;
    }
    if (burnoutChange && burnoutChange > 10) {
      return `Burnout risk is rising (${burnoutChange.toFixed(1)}% increase)—consider taking a break or discussing workload with your manager.`;
    }
    if (motivationChange && motivationChange < -10) {
      return `Motivation has decreased ${Math.abs(motivationChange).toFixed(1)}%—this may indicate the need for support or engagement activities.`;
    }
    if (wellbeingChange && wellbeingChange < -5) {
      return `Wellbeing has declined ${Math.abs(wellbeingChange).toFixed(1)}%—consider wellness activities or support resources.`;
    }
    return `Your metrics are relatively stable compared to the previous ${periodType === 'weekly' ? 'week' : 'month'}.`;
  };

  return {
    periodStart,
    periodEnd,
    currentMetrics,
    previousMetrics,
    changes,
    topFlags,
    insight: generateInsight()
  };
}

function getCoachingSuggestions(metrics) {
  const tips = [];
  if (metrics.emotion < 30) {
    tips.push("Schedule a brief 1:1 to explore current challenges and emotions; focus on listening and validation.");
  } else if (metrics.emotion < 60) {
    tips.push("Encourage short daily reflection or journaling to track mood shifts and triggers.");
  } else {
    tips.push("Maintain regular check‑ins to reinforce positive emotional patterns and recognise recent wins.");
  }
  if (metrics.motivation < 40) {
    tips.push("Clarify priorities and set one or two achievable goals for this week to rebuild motivation.");
  }
  if (metrics.burnout >= 70) {
    tips.push("Discuss workload, boundaries, and recovery time; consider temporary task redistribution or breaks.");
  } else if (metrics.burnout >= 40) {
    tips.push("Introduce simple recovery rituals (walks, micro‑breaks, no-meeting blocks) and monitor for fatigue signs.");
  }
  if (metrics.stress >= 60) {
    tips.push("Teach one quick stress technique such as 2–3 minutes of slow breathing before/after demanding tasks.");
  }
  if (metrics.engagement < 40) {
    tips.push("Connect work to strengths and interests; identify one task that feels meaningful and expand on it.");
  }
  if (metrics.wellbeing < 40) {
    tips.push("Encourage basic self‑care: sleep routine, movement, and social support; check if further support is needed.");
  }
  if (!tips.length) {
    tips.push("Maintain current habits and schedule a follow‑up session to keep progress on track.");
  }
  return tips.slice(0, 5);
}

export default function EmployeeDetailModal({
  employee,
  sessions,
  loadingSessions,
  onClose,
  onDelete,
  onConductSession,
  departments,
  t
}) {
  const [reportTab, setReportTab] = useState('current');

  const timelineData = useMemo(() => {
    return prepareSessionTrendData(sessions, { includeWellbeing: true, sortAscending: true });
  }, [sessions]);

  const radarData = useMemo(() => {
    if (!sessions || sessions.length === 0) return [];
    const latest = sessions[0];
    const previous = sessions.length > 1 ? sessions[1] : null;
    return [
      { subject: 'Emotion', current: parseFloat(latest.emotion_score) || 0, previous: previous ? parseFloat(previous.emotion_score) || 0 : null },
      { subject: 'Motivation', current: parseFloat(latest.motivation_score) || 0, previous: previous ? parseFloat(previous.motivation_score) || 0 : null },
      { subject: 'Engagement', current: parseFloat(latest.engagement_score) || 0, previous: previous ? parseFloat(previous.engagement_score) || 0 : null },
      { subject: 'Wellbeing', current: parseFloat(latest.wellbeing_score) || 0, previous: previous ? parseFloat(previous.wellbeing_score) || 0 : null },
      { subject: 'Stress Relief', current: 100 - (parseFloat(latest.stress_score) || 0), previous: previous ? 100 - (parseFloat(previous.stress_score) || 0) : null },
      { subject: 'Burnout Relief', current: 100 - (parseFloat(latest.burnout_score) || 0), previous: previous ? 100 - (parseFloat(previous.burnout_score) || 0) : null },
    ];
  }, [sessions]);

  const trendData = useMemo(() => {
    return prepareSessionTrendData(sessions);
  }, [sessions]);

  const flags = useMemo(() => {
    return generateSessionFlags(sessions);
  }, [sessions]);

  const latestSession = sessions && sessions.length > 0 ? sessions[0] : null;

  const latestScores = useMemo(() => {
    return latestSession ? {
      emotion: parseFloat(latestSession.emotion_score) || 0,
      motivation: parseFloat(latestSession.motivation_score) || 0,
      burnout: parseFloat(latestSession.burnout_score) || 0,
      engagement: parseFloat(latestSession.engagement_score) || 0,
      stress: parseFloat(latestSession.stress_score) || 0,
      wellbeing: parseFloat(latestSession.wellbeing_score) || 0
    } : { emotion: 0, motivation: 0, burnout: 0, engagement: 0, stress: 0, wellbeing: 0 };
  }, [latestSession]);

  const coachingSuggestions = useMemo(() => {
    return getCoachingSuggestions(latestScores);
  }, [latestScores]);

  const handleClose = () => {
    onClose();
  };

  const handleConductSession = () => {
    onConductSession(employee);
  };

  if (loadingSessions) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="employee-modal employee-modal-enhanced" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{employee.name}{employee.department ? ` - ${employee.department}` : ''}</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
          <div className="loading-sessions">{t('Loading session data...')}</div>
        </div>
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="employee-modal employee-modal-enhanced" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{employee.name}{employee.department ? ` - ${employee.department}` : ''}</h2>
            <div className="modal-header-actions">
              <button
                type="button"
                className="employee-modal-delete-btn"
                onClick={() => onDelete(employee)}
                title="Delete staff member"
              >
                🗑️ Delete staff
              </button>
              <button className="modal-close" onClick={handleClose}>×</button>
            </div>
          </div>
          <div className="no-sessions-container">
            <div className="no-sessions-icon">📊</div>
            <h3 className="no-sessions-title">{t('No Sessions Conducted Yet')}</h3>
            <p className="no-sessions-message">
              {t("This staff member hasn't had any wellness sessions conducted yet.")}
              {t('Conduct a session to view detailed analytics, trends, and personalized coaching suggestions.')}
            </p>
            <div className="no-sessions-actions">
              <button className="conduct-session-from-modal-btn" onClick={handleConductSession}>
                🎤 {t('Conduct Session Now')}
              </button>
            </div>
            <div className="no-sessions-info">
              <h4>{t("What you'll see after conducting a session:")}</h4>
              <ul>
                <li>📊 {t('Comprehensive wellness metrics (Emotion, Motivation, Burnout, etc.)')}</li>
                <li>📈 {t('Trend analysis and timeline charts')}</li>
                <li>🎯 {t('Wellness radar visualization')}</li>
                <li>⚠️ {t('Risk assessment and flags')}</li>
                <li>💡 {t('Personalized coaching suggestions')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="employee-modal employee-modal-enhanced" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{employee.name}{employee.department ? ` - ${employee.department}` : ''}</h2>
          <div className="modal-header-actions">
            <button
              type="button"
              className="employee-modal-delete-btn"
              onClick={() => onDelete(employee)}
              title="Delete staff member"
            >
              🗑️ Delete staff
            </button>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
        </div>

        <div className="employee-report-tabs">
          <button className={`report-tab ${reportTab === 'current' ? 'active' : ''}`} onClick={() => setReportTab('current')}>
            📊 {t('Current')}
          </button>
          <button className={`report-tab ${reportTab === 'weekly' ? 'active' : ''}`} onClick={() => setReportTab('weekly')}>
            📅 {t('Weekly Report')}
          </button>
          <button className={`report-tab ${reportTab === 'monthly' ? 'active' : ''}`} onClick={() => setReportTab('monthly')}>
            📆 {t('Monthly Report')}
          </button>
        </div>

        {reportTab === 'current' && (
          <>
            {sessions.length > 0 && (
              <div className="modal-section overview-section">
                <h3>📊 {t('Comprehensive Report Overview')}</h3>
                <div className="overview-stats">
                  <div className="overview-stat">
                    <div className="stat-label">{t('Total Sessions')}</div>
                    <div className="stat-value">{sessions.length}</div>
                  </div>
                  <div className="overview-stat">
                    <div className="stat-label">{t('Average Emotion')}</div>
                    <div className="stat-value">
                      {timelineData.length > 0
                        ? (timelineData.reduce((sum, d) => sum + d.emotion, 0) / timelineData.length).toFixed(1)
                        : latestScores.emotion.toFixed(1)}
                    </div>
                  </div>
                  <div className="overview-stat">
                    <div className="stat-label">{t('Average Motivation')}</div>
                    <div className="stat-value">
                      {timelineData.length > 0
                        ? (timelineData.reduce((sum, d) => sum + d.motivation, 0) / timelineData.length).toFixed(1)
                        : latestScores.motivation.toFixed(1)}
                    </div>
                  </div>
                  <div className="overview-stat">
                    <div className="stat-label">{t('Risk Level')}</div>
                    <div className={`stat-value risk-${latestScores.burnout > 70 ? 'high' : latestScores.burnout > 50 ? 'moderate' : 'low'}`}>
                      {latestScores.burnout > 70 ? 'High' : latestScores.burnout > 50 ? 'Moderate' : 'Low'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="modal-section scores-summary">
              <h3>{t('Current Metrics')}</h3>
              <div className="scores-grid">
                <div className="score-card emotion">
                  <div className="score-label">{t('Emotion')}</div>
                  <div className="score-value">{latestScores.emotion.toFixed(1)}</div>
                </div>
                <div className="score-card motivation">
                  <div className="score-label">{t('Motivation')}</div>
                  <div className="score-value">{latestScores.motivation.toFixed(1)}</div>
                </div>
                <div className="score-card burnout">
                  <div className="score-label">{t('Burnout')}</div>
                  <div className="score-value">{latestScores.burnout.toFixed(1)}</div>
                </div>
                <div className="score-card engagement">
                  <div className="score-label">{t('Engagement')}</div>
                  <div className="score-value">{latestScores.engagement.toFixed(1)}</div>
                </div>
                <div className="score-card stress">
                  <div className="score-label">{t('Stress')}</div>
                  <div className="score-value">{latestScores.stress.toFixed(1)}</div>
                </div>
                <div className="score-card wellbeing">
                  <div className="score-label">{t('Wellbeing')}</div>
                  <div className="score-value">{latestScores.wellbeing.toFixed(1)}</div>
                </div>
              </div>
            </div>

            <div className="modal-content-grid">
              <div className="modal-content-column">
                {radarData.length > 0 && (
                  <div className="modal-section chart-section">
                    <h3>{t('Wellness Radar - Current vs Previous Session')}</h3>
                    <div className="radar-chart-container">
                      <ResponsiveContainer width="100%" height={350}>
                        <RadarChart data={radarData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="subject" tick={{ fill: '#374151', fontSize: 12 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 10 }} />
                          <Radar name="Current Session" dataKey="current" stroke="#8884d8" fill="#8884d8" fillOpacity={0.7} />
                          {radarData.some(d => d.previous !== null && d.previous !== undefined) && (
                            <Radar name="Previous Session" dataKey="previous" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.5} />
                          )}
                          <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                          <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="radar-legend">
                      <div className="legend-item"><span className="legend-color" style={{ background: '#8884d8' }}></span> Current Session</div>
                      {radarData.some(d => d.previous !== null && d.previous !== undefined) && (
                        <div className="legend-item"><span className="legend-color" style={{ background: '#82ca9d' }}></span> Previous Session</div>
                      )}
                    </div>
                  </div>
                )}

                {timelineData.length > 0 ? (
                  <div className="modal-section chart-section">
                    <h3>📈 {t('Comprehensive Metrics Timeline')}</h3>
                    <div className="timeline-chart-container">
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={timelineData}>
                          <defs>
                            <linearGradient id="colorEmotion" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorMotivation" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#ffc658" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#ffc658" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                          <Legend />
                          <Area type="monotone" dataKey="emotion" stackId="1" stroke="#8884d8" fill="url(#colorEmotion)" name="Emotion" />
                          <Area type="monotone" dataKey="motivation" stackId="2" stroke="#82ca9d" fill="url(#colorMotivation)" name="Motivation" />
                          <Area type="monotone" dataKey="engagement" stackId="3" stroke="#ffc658" fill="url(#colorEngagement)" name="Engagement" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="modal-section chart-section">
                    <h3>{t('Emotion Timeline')}</h3>
                    <p className="no-data-message">{t('No timeline data available. Conduct a session to see emotion trends.')}</p>
                  </div>
                )}
              </div>

              <div className="modal-content-column">
                {latestScores && (
                  <div className="modal-section chart-section">
                    <h3>⚠️ {t('Risk Assessment')}</h3>
                    <div className="risk-chart-container">
                      <div className="risk-pie-chart">
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Low Risk', value: latestScores.burnout < 50 ? 100 - latestScores.burnout : 50 },
                                { name: 'Moderate Risk', value: latestScores.burnout >= 50 && latestScores.burnout < 70 ? latestScores.burnout - 50 : 0 },
                                { name: 'High Risk', value: latestScores.burnout >= 70 ? latestScores.burnout - 70 : 0 }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              <Cell fill="#10b981" />
                              <Cell fill="#f59e0b" />
                              <Cell fill="#ef4444" />
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="risk-summary-text">
                        <div className="risk-item">
                          <span className="risk-indicator low"></span>
                          <span>Low Risk: {latestScores.burnout < 50 ? '✓' : '✗'}</span>
                        </div>
                        <div className="risk-item">
                          <span className="risk-indicator moderate"></span>
                          <span>Moderate Risk: {latestScores.burnout >= 50 && latestScores.burnout < 70 ? '⚠' : '✓'}</span>
                        </div>
                        <div className="risk-item">
                          <span className="risk-indicator high"></span>
                          <span>High Risk: {latestScores.burnout >= 70 ? '⚠' : '✓'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {trendData.length > 0 && (
                  <div className="modal-section chart-section">
                    <h3>{t('Trend Analysis')}</h3>
                    <div className="trend-charts-grid">
                      <div className="trend-chart-item">
                        <h4>{t('Burnout Trend')}</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Bar dataKey="burnout" fill="#ff4444" name="Burnout" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="trend-chart-item">
                        <h4>{t('Stress Trend')}</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Bar dataKey="stress" fill="#ff8800" name="Stress" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="trend-chart-item">
                        <h4>{t('Engagement Trend')}</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={trendData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis domain={[0, 100]} />
                            <Tooltip />
                            <Line type="monotone" dataKey="engagement" stroke="#00aa00" strokeWidth={2} name="Engagement" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal-section">
                  <h3>{t('Flag History')}</h3>
                  <div className="flag-history">
                    {flags.length > 0 ? (
                      flags.map((flag, index) => (
                        <div key={index} className={`flag-item ${flag.severity}`}>
                          <span className="flag-icon">{flag.severity === 'high' ? '🔴' : '🟡'}</span>
                          <div className="flag-content">
                            <span className="flag-type">{flag.type}</span>
                            <span className="flag-date">{flag.date}</span>
                          </div>
                          <span className="flag-status">{flag.resolved ? '✅' : '⚠️'}</span>
                        </div>
                      ))
                    ) : (
                      <p className="no-data-message">{t('No flags recorded. All metrics are within normal ranges.')}</p>
                    )}
                  </div>
                </div>

                <SessionListPanel sessions={sessions} t={t} />
              </div>
            </div>

            <div className="modal-section employee-coaching-card">
              <h3>📋 {t('Coaching Suggestions')}</h3>
              <p className="coaching-summary">{t("Based on this session's scores, focus on the following next steps:")}</p>
              <ul className="coaching-suggestions-list">
                {coachingSuggestions.map((tip, index) => (
                  <li key={index} className="coaching-suggestion-item">{tip}</li>
                ))}
              </ul>
            </div>
          </>
        )}

        {reportTab === 'weekly' && (() => {
          const sessionsArr = Array.isArray(sessions) ? sessions : (sessions ? [sessions] : []);
          const weeklyData = calculatePeriodMetrics(sessionsArr, 'weekly');
          if (!weeklyData || !weeklyData.currentMetrics) {
            return (
              <div className="report-no-data">
                <p>Not enough data to generate weekly report.</p>
                <p className="report-hint">Conduct sessions to generate report data.</p>
              </div>
            );
          }
          const currentMetrics = weeklyData.currentMetrics || {};
          const changes = weeklyData.changes || null;
          const topFlags = weeklyData.topFlags || [];
          const previousMetrics = weeklyData.previousMetrics || null;

          return (
            <div className="period-report-content">
              <div className="report-header-section">
                <h3>📅 Weekly Report</h3>
                <p className="report-period">
                  {weeklyData.periodStart?.toLocaleDateString() || 'N/A'} – {weeklyData.periodEnd?.toLocaleDateString() || 'N/A'}
                </p>
                <div className="report-insight">
                  <span className="insight-icon">💡</span>
                  <span className="insight-text">{weeklyData.insight || 'No insight available.'}</span>
                </div>
              </div>
              <div className="report-metrics-grid">
                {[
                  { label: 'Emotion', icon: '😊', value: currentMetrics.emotion || 0, change: changes?.emotion },
                  { label: 'Motivation', icon: '🚀', value: currentMetrics.motivation || 0, change: changes?.motivation },
                  { label: 'Burnout', icon: '🔥', value: currentMetrics.burnout || 0, change: changes?.burnout },
                  { label: 'Engagement', icon: '💼', value: currentMetrics.engagement || 0, change: changes?.engagement },
                  { label: 'Stress', icon: '😰', value: currentMetrics.stress || 0, change: changes?.stress },
                  { label: 'Wellbeing', icon: '❤️', value: currentMetrics.wellbeing || 0, change: changes?.wellbeing }
                ].map(metric => (
                  <div key={metric.label} className="report-metric-card">
                    <div className="metric-header">
                      <span className="metric-icon">{metric.icon}</span>
                      <span className="metric-label">{metric.label}</span>
                    </div>
                    <div className="metric-value-large">{metric.value.toFixed(1)}</div>
                    {metric.change !== null && metric.change !== undefined && (
                      <div className={`metric-change ${metric.change > 0 && metric.label !== 'Burnout' && metric.label !== 'Stress' ? 'positive' : metric.change < 0 && (metric.label === 'Burnout' || metric.label === 'Stress') ? 'positive' : 'negative'}`}>
                        {metric.change > 0 ? '↑' : '↓'} {Math.abs(metric.change).toFixed(1)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="report-summary-section">
                <div className="summary-card">
                  <h4>📊 Summary</h4>
                  <div className="summary-item">
                    <span>Total Sessions:</span>
                    <span className="summary-value">{currentMetrics.totalSessions || 0}</span>
                    {changes && changes.sessions !== null && changes.sessions !== undefined && (
                      <span className={`summary-change ${changes.sessions > 0 ? 'positive' : 'negative'}`}>
                        {changes.sessions > 0 ? '+' : ''}{changes.sessions.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
                {topFlags && topFlags.length > 0 && (
                  <div className="summary-card">
                    <h4>⚠️ Top Flagged Issues</h4>
                    <div className="flags-list">
                      {topFlags.map((flag, idx) => (
                        <div key={idx} className={`flag-item ${flag.severity}`}>
                          <span className="flag-icon">{flag.severity === 'high' ? '🔴' : '🟡'}</span>
                          <span className="flag-text">{flag.type}</span>
                          <span className="flag-value">{flag.value}%</span>
                          <span className="flag-date">{new Date(flag.date).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {previousMetrics && (
                <div className="report-trend-chart">
                  <h4>Trend Comparison</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={[
                      { period: 'Previous Week', emotion: previousMetrics.emotion || 0, motivation: previousMetrics.motivation || 0, engagement: previousMetrics.engagement || 0 },
                      { period: 'Current Week', emotion: currentMetrics.emotion || 0, motivation: currentMetrics.motivation || 0, engagement: currentMetrics.engagement || 0 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="emotion" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} name="Emotion" />
                      <Area type="monotone" dataKey="motivation" stackId="2" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} name="Motivation" />
                      <Area type="monotone" dataKey="engagement" stackId="3" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} name="Engagement" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          );
        })()}

        {reportTab === 'monthly' && (() => {
          const sessionsArr = Array.isArray(sessions) ? sessions : (sessions ? [sessions] : []);
          const monthlyData = calculatePeriodMetrics(sessionsArr, 'monthly');
          if (!monthlyData || !monthlyData.currentMetrics) {
            return (
              <div className="report-no-data">
                <p>Not enough data to generate monthly report.</p>
                <p className="report-hint">Conduct sessions to generate report data.</p>
              </div>
            );
          }
          const currentMetrics = monthlyData.currentMetrics || {};
          const changes = monthlyData.changes || null;
          const topFlags = monthlyData.topFlags || [];
          const previousMetrics = monthlyData.previousMetrics || null;

          return (
            <div className="period-report-content">
              <div className="report-header-section">
                <h3>📆 Monthly Report</h3>
                <p className="report-period">
                  {monthlyData.periodStart?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) || 'N/A'}
                </p>
                <div className="report-insight">
                  <span className="insight-icon">💡</span>
                  <span className="insight-text">{monthlyData.insight || 'No insight available.'}</span>
                </div>
              </div>
              <div className="report-metrics-grid">
                {[
                  { label: 'Emotion', icon: '😊', value: currentMetrics.emotion || 0, change: changes?.emotion },
                  { label: 'Motivation', icon: '🚀', value: currentMetrics.motivation || 0, change: changes?.motivation },
                  { label: 'Burnout', icon: '🔥', value: currentMetrics.burnout || 0, change: changes?.burnout },
                  { label: 'Engagement', icon: '💼', value: currentMetrics.engagement || 0, change: changes?.engagement },
                  { label: 'Stress', icon: '😰', value: currentMetrics.stress || 0, change: changes?.stress },
                  { label: 'Wellbeing', icon: '❤️', value: currentMetrics.wellbeing || 0, change: changes?.wellbeing }
                ].map(metric => (
                  <div key={metric.label} className="report-metric-card">
                    <div className="metric-header">
                      <span className="metric-icon">{metric.icon}</span>
                      <span className="metric-label">{metric.label}</span>
                    </div>
                    <div className="metric-value-large">{metric.value.toFixed(1)}</div>
                    {metric.change !== null && metric.change !== undefined && (
                      <div className={`metric-change ${metric.change > 0 && metric.label !== 'Burnout' && metric.label !== 'Stress' ? 'positive' : metric.change < 0 && (metric.label === 'Burnout' || metric.label === 'Stress') ? 'positive' : 'negative'}`}>
                        {metric.change > 0 ? '↑' : '↓'} {Math.abs(metric.change).toFixed(1)}%
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="report-summary-section">
                <div className="summary-card">
                  <h4>📊 Summary</h4>
                  <div className="summary-item">
                    <span>Total Sessions:</span>
                    <span className="summary-value">{currentMetrics.totalSessions || 0}</span>
                    {changes && changes.sessions !== null && changes.sessions !== undefined && (
                      <span className={`summary-change ${changes.sessions > 0 ? 'positive' : 'negative'}`}>
                        {changes.sessions > 0 ? '+' : ''}{changes.sessions.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
                {topFlags && topFlags.length > 0 && (
                  <div className="summary-card">
                    <h4>⚠️ Top Flagged Issues</h4>
                    <div className="flags-list">
                      {topFlags.map((flag, idx) => (
                        <div key={idx} className={`flag-item ${flag.severity}`}>
                          <span className="flag-icon">{flag.severity === 'high' ? '🔴' : '🟡'}</span>
                          <span className="flag-text">{flag.type}</span>
                          <span className="flag-value">{flag.value}%</span>
                          <span className="flag-date">{new Date(flag.date).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {previousMetrics && (
                <div className="report-trend-chart">
                  <h4>Trend Comparison</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={[
                      { period: 'Previous Month', emotion: previousMetrics.emotion || 0, motivation: previousMetrics.motivation || 0, engagement: previousMetrics.engagement || 0 },
                      { period: 'Current Month', emotion: currentMetrics.emotion || 0, motivation: currentMetrics.motivation || 0, engagement: currentMetrics.engagement || 0 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="emotion" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} name="Emotion" />
                      <Area type="monotone" dataKey="motivation" stackId="2" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} name="Motivation" />
                      <Area type="monotone" dataKey="engagement" stackId="3" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} name="Engagement" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
